import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { academicService, adminService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { useConfirm } from '../../contexts/ConfirmContext';
import { Card, Badge, Button, Modal, Input, Field, EmptyState, Spinner, Pagination } from '../../components/ui';

const EMPTY_FORM = { name: '', code: '', order: 0, is_active: true };
const PAGE_SIZE = 20;

function AdminGrades() {
  const { addNotification } = useNotification();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const [grades, setGrades] = useState([]);
  const [modules, setModules] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  // Grade roster modal — courses attached to the grade (with their
  // teachers), and the grade's allocated students (Grade.students, the
  // admin-managed roster — not derived from Enrollment, since a grade can
  // have allocated students before it has any courses at all).
  const [rosterGrade, setRosterGrade] = useState(null);
  const [rosterTab, setRosterTab] = useState('courses');

  // Allocation modals — checkbox lists stacked above the roster modal.
  const [allocateType, setAllocateType] = useState(null); // 'courses' | 'students' | null
  const [allocateSelectedIds, setAllocateSelectedIds] = useState([]);
  const [allocateSearch, setAllocateSearch] = useState('');
  const [allocating, setAllocating] = useState(false);

  const fetchGrades = () => {
    setLoading(true);
    return Promise.all([
      academicService.getGrades({ page, page_size: PAGE_SIZE }),
      academicService.getModules({ page_size: 100 }),
      adminService.getUsers({ role: 'student', page_size: 100 }),
    ])
      .then(([gradesRes, modulesRes, studentsRes]) => {
        const gradesData = gradesRes.data;
        const newGrades = Array.isArray(gradesData) ? gradesData : gradesData.results || [];
        setGrades(newGrades);
        setCount(Array.isArray(gradesData) ? gradesData.length : gradesData.count || 0);
        const modulesData = modulesRes.data;
        setModules(Array.isArray(modulesData) ? modulesData : modulesData.results || []);
        const studentsData = studentsRes.data;
        setStudents(Array.isArray(studentsData) ? studentsData : studentsData.results || []);
        return newGrades;
      })
      .catch(() => {
        addNotification(t('Failed to load grade levels'), 'error');
        return [];
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchGrades(); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const modulesForGrade = (grade) => modules.filter((m) => (m.grades || []).some((g) => g.id === grade.id));

  const openRoster = (grade) => {
    setRosterGrade(grade);
    setRosterTab('courses');
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (g) => { setEditing(g); setForm({ ...g }); setModalOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await academicService.updateGrade(editing.id, form);
      } else {
        await academicService.createGrade(form);
      }
      await fetchGrades();
      addNotification(t('Grade level saved'), 'success');
      setModalOpen(false);
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, 'Failed to save grade level'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (g) => {
    if (!(await confirm(t('Delete grade "{{name}}"?', { name: g.name })))) return;
    try {
      await academicService.deleteGrade(g.id);
      setGrades((prev) => prev.filter((x) => x.id !== g.id));
      setCount((current) => Math.max(0, current - 1));
      addNotification(t('Grade level deleted'), 'success');
    } catch (e) {
      addNotification(t('Failed to delete (it may be in use by modules/enrollments)'), 'error');
    }
  };

  const openAllocate = (type) => {
    setAllocateSearch('');
    if (type === 'courses') {
      setAllocateSelectedIds(modulesForGrade(rosterGrade).map((m) => m.id));
    } else {
      setAllocateSelectedIds((rosterGrade.students || []).map((s) => s.id));
    }
    setAllocateType(type);
  };

  const toggleAllocateId = (id) => {
    setAllocateSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const saveAllocation = async () => {
    setAllocating(true);
    try {
      if (allocateType === 'courses') {
        await academicService.allocateGradeCourses(rosterGrade.id, allocateSelectedIds);
      } else {
        await academicService.allocateGradeStudents(rosterGrade.id, allocateSelectedIds);
      }
      const newGrades = await fetchGrades();
      const refreshed = newGrades.find((g) => g.id === rosterGrade.id);
      if (refreshed) setRosterGrade(refreshed);
      addNotification(t('Allocation saved'), 'success');
      setAllocateType(null);
    } catch (e) {
      addNotification(t('Failed to save allocation'), 'error');
    } finally {
      setAllocating(false);
    }
  };

  const allocateOptions = allocateType === 'courses' ? modules : students;
  const filteredAllocateOptions = allocateOptions.filter((opt) => {
    if (!allocateSearch) return true;
    const q = allocateSearch.toLowerCase();
    if (allocateType === 'courses') return opt.name.toLowerCase().includes(q) || opt.code.toLowerCase().includes(q);
    return `${opt.first_name} ${opt.last_name}`.toLowerCase().includes(q) || opt.email.toLowerCase().includes(q);
  });

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Grade Levels')} subtitle={t('Manage the academic grade levels offered on the platform')} />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="flex justify-end mb-4">
            <Button onClick={openCreate}>+ {t('New Grade Level')}</Button>
          </div>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : grades.length === 0 ? (
            <Card><EmptyState title={t('No grade levels yet')} action={<Button onClick={openCreate}>+ {t('New Grade Level')}</Button>} /></Card>
          ) : (
            <Card padded={false}>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3">{t('Name')}</th>
                    <th className="text-left px-4 py-3">{t('Code')}</th>
                    <th className="text-left px-4 py-3">{t('Order')}</th>
                    <th className="text-left px-4 py-3">{t('Status')}</th>
                    <th className="text-right px-4 py-3">{t('Actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {grades.sort((a, b) => a.order - b.order).map((g) => (
                    <tr key={g.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{g.name}</td>
                      <td className="px-4 py-3 text-gray-600">{g.code}</td>
                      <td className="px-4 py-3 text-gray-600">{g.order}</td>
                      <td className="px-4 py-3"><Badge tone={g.is_active ? 'green' : 'gray'}>{g.is_active ? t('Active') : t('Inactive')}</Badge></td>
                      <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                        <Button size="sm" variant="secondary" onClick={() => openRoster(g)}>{t('View Details')}</Button>
                        <Button size="sm" variant="secondary" onClick={() => openEdit(g)}>{t('Edit')}</Button>
                        <Button size="sm" variant="danger" onClick={() => remove(g)}>{t('Delete')}</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination page={page} count={count} pageSize={PAGE_SIZE} onPageChange={setPage} />
            </Card>
          )}
        </main>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('Edit Grade Level') : t('New Grade Level')}
        footer={<>
          <Button variant="secondary" onClick={() => setModalOpen(false)}>{t('Cancel')}</Button>
          <Button loading={saving} onClick={save}>{t('Save')}</Button>
        </>}
      >
        <div className="space-y-4">
          <Field label={t('Name')} required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('e.g. 3rd Year Middle School')} />
          </Field>
          <Field label={t('Code')} required>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder={t('e.g. 3AM')} />
          </Field>
          <Field label={t('Display Order')}>
            <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            {t('Active')}
          </label>
        </div>
      </Modal>

      {/* Grade roster: courses (with their teachers) attached to this grade,
          and the students allocated to it. Allocating a student enrolls them
          in every course here automatically, and vice versa — order doesn't
          matter, see the "+ Allocate" buttons below. */}
      <Modal open={!!rosterGrade} onClose={() => setRosterGrade(null)} title={rosterGrade?.name} size="lg">
        {rosterGrade && (
          <>
            <div className="flex items-center justify-between gap-3 mb-4 border-b border-gray-100">
              <div className="flex gap-2">
                <button
                  onClick={() => setRosterTab('courses')}
                  className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                    rosterTab === 'courses' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('Courses')} ({modulesForGrade(rosterGrade).length})
                </button>
                <button
                  onClick={() => setRosterTab('students')}
                  className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                    rosterTab === 'students' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('Students')} ({(rosterGrade.students || []).length})
                </button>
              </div>
              <Button size="sm" onClick={() => openAllocate(rosterTab)} className="mb-2">
                + {rosterTab === 'courses' ? t('Allocate Courses') : t('Allocate Students')}
              </Button>
            </div>

            {rosterTab === 'courses' && (
              modulesForGrade(rosterGrade).length === 0 ? (
                <EmptyState title={t('No courses assigned to this grade yet')} />
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {modulesForGrade(rosterGrade).map((m) => (
                    <div key={m.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2">
                      <div>
                        <div className="font-medium text-gray-900 text-sm">{m.name}</div>
                        <div className="text-xs text-gray-400">{m.code}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">
                          {(m.teachers || []).length === 0 ? (
                            <span className="text-amber-600 text-xs font-medium">{t('No teacher assigned')}</span>
                          ) : (
                            (m.teachers || []).map((tch) => `${tch.first_name} ${tch.last_name}`).join(', ')
                          )}
                        </div>
                        <div className="text-xs text-gray-400">{t('{{count}} students', { count: m.students_count ?? 0 })}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            {rosterTab === 'students' && (
              (rosterGrade.students || []).length === 0 ? (
                <EmptyState title={t('No students allocated to this grade yet')} />
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {(rosterGrade.students || []).map((student) => (
                    <div key={student.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2">
                      <div>
                        <div className="font-medium text-gray-900 text-sm">{student.first_name} {student.last_name}</div>
                        <div className="text-xs text-gray-500">{student.email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </>
        )}
      </Modal>

      {/* Allocation modal — stacked above the roster modal (z-[60]). A full
          checkbox list of all courses or all students, pre-checked with the
          grade's current membership; saving replaces the set. */}
      <Modal
        open={!!allocateType}
        onClose={() => setAllocateType(null)}
        title={allocateType === 'courses' ? t('Allocate Courses') : t('Allocate Students')}
        size="lg"
        zIndexClass="z-[60]"
        footer={<>
          <Button variant="secondary" onClick={() => setAllocateType(null)}>{t('Cancel')}</Button>
          <Button loading={allocating} onClick={saveAllocation}>{t('Save')}</Button>
        </>}
      >
        <Input
          placeholder={allocateType === 'courses' ? t('Search by name or code...') : t('Search by name or email...')}
          value={allocateSearch}
          onChange={(e) => setAllocateSearch(e.target.value)}
          className="mb-3"
        />
        {allocateType === 'students' && (
          <p className="text-xs text-gray-500 mb-3">
            {t('A student allocated here is automatically enrolled in every course this grade has — including ones added later.')}
          </p>
        )}
        <div className="border border-gray-200 rounded-lg divide-y max-h-96 overflow-y-auto">
          {filteredAllocateOptions.length === 0 ? (
            <p className="text-sm text-gray-500 p-3">{t('No results')}</p>
          ) : (
            filteredAllocateOptions.map((opt) => (
              <label key={opt.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allocateSelectedIds.includes(opt.id)}
                  onChange={() => toggleAllocateId(opt.id)}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                {allocateType === 'courses' ? (
                  <span className="text-sm text-gray-700">{opt.name} <span className="text-gray-400">({opt.code})</span></span>
                ) : (
                  <span className="text-sm text-gray-700">{opt.first_name} {opt.last_name} <span className="text-gray-400">({opt.email})</span></span>
                )}
              </label>
            ))
          )}
        </div>
      </Modal>
    </div>
  );
}

export default AdminGrades;
